import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api, { formatINR } from '@/lib/api';
import { toast } from 'sonner';

const STATUS_FLOW = {
  placed: [{ to: 'accepted', label: 'Accept order', style: 'bg-olive-700 hover:bg-olive-800' }, { to: 'rejected', label: 'Reject', style: 'bg-rose-600 hover:bg-rose-700' }],
  accepted: [{ to: 'packing', label: 'Start packing', style: 'bg-terracotta-600 hover:bg-terracotta-700' }],
  packing: [{ to: 'ready', label: 'Mark ready', style: 'bg-olive-700 hover:bg-olive-800' }],
  ready: [{ to: 'out_for_delivery', label: 'Out for delivery', style: 'bg-olive-700 hover:bg-olive-800' }, { to: 'delivered', label: 'Mark delivered', style: 'bg-stone-800 hover:bg-stone-900' }],
  out_for_delivery: [{ to: 'delivered', label: 'Mark delivered', style: 'bg-stone-800 hover:bg-stone-900' }],
};

export default function SellerOrders() {
  const { seller } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sellers/me/orders', { params: filter ? { status: filter } : {} });
      setOrders(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (seller?.status === 'approved') load(); /* eslint-disable-next-line */ }, [seller, filter]);

  const act = async (order, to, reason) => {
    try {
      await api.patch(`/sellers/me/orders/${order.id}`, { status: to, reason });
      toast.success(`Order ${to}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (seller?.status !== 'approved') return <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-600">Orders will appear here after your store is approved.</div>;

  const tabs = ['', 'placed', 'accepted', 'packing', 'ready', 'out_for_delivery', 'delivered', 'rejected'];

  return (
    <div className="space-y-5" data-testid="seller-orders">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-3xl text-stone-900">Orders</h1><p className="text-sm text-stone-500">{orders.length} shown</p></div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button key={t||'all'} onClick={() => setFilter(t)} data-testid={`seller-tab-${t||'all'}`}
            className={`h-8 px-3 rounded-full text-xs font-semibold ${filter === t ? 'bg-terracotta-600 text-white' : 'bg-white border border-stone-300 text-stone-700 hover:bg-stone-50'}`}>
            {t ? t.replace(/_/g, ' ').toUpperCase() : 'ALL'}
          </button>
        ))}
      </div>

      {loading ? <p className="text-stone-500">Loading…</p> : orders.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-200 rounded-2xl p-10 text-center text-stone-500">No orders.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const actions = STATUS_FLOW[o.status] || [];
            return (
              <div key={o.id} data-testid={`seller-order-${o.order_no}`} className="bg-white rounded-2xl border border-stone-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs text-stone-500">{o.order_no}</p>
                    <p className="font-heading text-lg text-stone-900">{o.customer_name} · {o.customer_phone}</p>
                    <p className="text-xs text-stone-500">{o.address?.line1}, {o.address?.city} - {o.address?.pincode}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl text-stone-900">{formatINR(o.total)}</p>
                    <p className="text-xs text-stone-500">Commission {o.commission_pct}% = {formatINR(o.commission_amount)}</p>
                    <p className="text-sm font-semibold text-olive-800">Net {formatINR(o.net_payable)}</p>
                  </div>
                </div>
                <div className="mt-3 text-sm">
                  {o.items?.map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1 border-t border-stone-100 first:border-t-0">
                      <span>{it.quantity} × {it.name}</span>
                      <span className="text-stone-600">{formatINR((it.price || 0) * (it.quantity || 0))}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] font-semibold px-2 py-1 rounded-full bg-stone-100 text-stone-700 uppercase`}>{o.status.replace(/_/g, ' ')}</span>
                  {actions.map((a) => (
                    <button
                      key={a.to}
                      data-testid={`seller-order-action-${a.to}`}
                      onClick={() => a.to === 'rejected' ? act(o, 'rejected', window.prompt('Reason (optional)') || '') : act(o, a.to)}
                      className={`h-9 px-4 rounded-full text-white text-xs font-semibold ${a.style}`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
